import React, { useState } from "react";
import { CheckCircle } from "lucide-react";
import Particles from "../../../Particels";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const AboPage = () => {
  const { language } = useLanguage();
  const text = dashboardContent[language].subscriptionPage;
  const plans = text.plans;

  const [billingCycle, setBillingCycle] = useState("monthly");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleToggle = () => {
    setBillingCycle((prev) => (prev === "monthly" ? "annual" : "monthly"));
  };

  const handleSubscribe = async (plan) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/create-checkout-session`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
          body: JSON.stringify({ plan, billingCycle }),
        }
      );

      if (!response.ok) throw new Error("Failed to create checkout session");

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error("Error creating checkout session:", error.message);
    }
  };

  const renderAgbText = () => (
    <span className="text-sm text-gray-600">
      {language === "de" ? "Ich akzeptiere die " : "I accept the "}
      <a
        href={text.agbLink}
        className="text-orange-500 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {language === "de" ? "AGB" : "Terms of Service"}
      </a>
      {" " + (language === "de" ? "und" : "and") + " "}
      <a
        href={text.datenschutzLink}
        className="text-orange-500 underline"
        target="_blank"
        rel="noopener noreferrer"
      >
        {language === "de" ? "Datenschutzrichtlinien" : "Privacy Policy"}
      </a>
      .
    </span>
  );

  return (
    <>
      <Particles />
      <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4">
        <h1 className="mb-6 text-2xl font-bold">{text.title}</h1>
        <p className="mb-6 text-center text-sm text-gray-700 max-w-md">
          {text.infoText}
        </p>

        {/* Billing Cycle Toggle */}
        <div className="flex items-center mb-6">
          <span className="text-gray-700 font-medium mr-4">{text.monthly}</span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={billingCycle === "annual"}
              onChange={handleToggle}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-orange-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-5 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500" />
          </label>
          <span className="text-gray-700 font-medium ml-4">{text.annual}</span>
        </div>

        {/* AGB checkbox */}
        <div className="flex items-center mb-8 gap-2">
          <input
            type="checkbox"
            className="accent-orange-500"
            checked={agreedToTerms}
            onChange={() => setAgreedToTerms((prev) => !prev)}
          />
          {renderAgbText()}
        </div>
        {/* Rabattcode Hinweis */}
        <div className="mb-6 text-sm text-center text-orange-600 font-medium bg-orange-50 px-4 py-2 rounded-md border border-orange-200">
          🎁 {language === "de" ? "Rabattcode " : "Discount code "}
          <span className="font-semibold">START20</span> –{" "}
          {language === "de"
            ? "20% Rabatt im ersten Monat!"
            : "20% off your first month!"}
        </div>

        {/* Plans */}
        <div className="flex flex-col md:flex-row gap-8">
          {plans.map((plan) => {
            const price =
              billingCycle === "monthly" ? plan.monthlyPrice : plan.annualPrice;
            const period =
              billingCycle === "monthly"
                ? text.monthlyPeriod
                : text.annualPeriod;

            return (
              <div
                key={plan.name}
                className="bg-white z-10 shadow-lg rounded-lg p-6 w-72 text-center flex flex-col border border-gray-100 hover:shadow-xl transition-shadow duration-200"
              >
                <h2 className="text-lg font-bold mb-2 text-gray-800">
                  {plan.name}
                </h2>
                <p className="text-2xl font-semibold mb-4 text-gray-900">
                  {price.toFixed(2)}€
                  <span className="text-base font-normal text-gray-600">
                    {" "}
                    / {period}
                  </span>
                </p>

                <ul className="flex-1 mb-4 space-y-2 text-gray-700 text-left">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="text-orange-500 w-5 h-5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.name.toUpperCase())}
                  className="mt-auto bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!agreedToTerms}
                >
                  {text.selectButton}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default AboPage;
